/**
 * Enhanced Gift Card Form Handler - Optimized Version
 * - Improved security with input sanitization
 * - Better validation for all fields
 * - Performance optimizations
 * - CSRF protection
 */
document.addEventListener('DOMContentLoaded', () => {
    // Constants for validation
    const CONSTANTS = {
        MIN_AMOUNT: 1.00,
        MAX_AMOUNT: 2000.00,
        EMAIL_REGEX: /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/,
        DEBOUNCE_DELAY: 300,
        RATE_LIMIT: { count: 0, lastSubmit: 0, maxSubmits: 15, timeWindow: 60000 }, // 15 submissions per minute
        // Common email providers for typo detection
        COMMON_EMAIL_PROVIDERS: [
            { correct: 'gmail.com', common: ['gmail', 'gmial.com', 'gmai.com', 'gmil.com', 'gmaill.com'] },
            { correct: 'yahoo.com', common: ['yahoo', 'yaho.com', 'yahooo.com'] },
            { correct: 'hotmail.com', common: ['hotmail', 'hotmai.com', 'hotmal.com'] },
            { correct: 'outlook.com', common: ['outlook', 'outlok.com', 'outloo.com'] },
            { correct: 'icloud.com', common: ['icloud', 'iclod.com', 'iclou.com'] },
            { correct: 'aol.com', common: ['aol', 'ao.com'] },
            { correct: 'protonmail.com', common: ['protonmail', 'proton.com'] },
            { correct: 'live.com', common: ['live'] },
            { correct: 'msn.com', common: ['msn'] },
            { correct: 'me.com', common: ['me'] }
        ],
        // Valid TLDs for final check
        VALID_TLDS: ['com', 'net', 'org', 'edu', 'gov', 'mil', 'int', 'co', 'io', 'us', 'uk', 'ca', 'au', 'de', 'fr', 'jp', 'cn', 'in', 'br', 'ru', 'it', 'es', 'nl', 'se', 'no', 'dk', 'fi', 'pl', 'be', 'ch', 'at', 'nz', 'sg', 'hk', 'kr', 'mx', 'info', 'biz', 'tv', 'cc', 'ws', 'mobi', 'name', 'pro', 'travel', 'jobs', 'tel', 'asia']
    };

    // Cache DOM elements with a more efficient approach
    const elements = {
        form: document.getElementById('gift-card-form')
    };
    
    // Only continue if form exists
    if (!elements.form) return;
    
    // Cache remaining elements
    Object.assign(elements, {
        variantInput: document.getElementById('product-variant-id'),
        customAmountInput: document.querySelector('[data-gift-card-amount]'),
        senderName: document.getElementById('sender-name'),
        senderEmail: document.getElementById('sender-email'),
        recipientName: document.getElementById('recipient-name'),
        recipientEmail: document.getElementById('recipient-email'),
        recipientEmailConfirm: document.getElementById('recipient-email-confirm'),
        emailMatchIndicator: document.querySelector('.email-match-indicator'),
        message: document.getElementById('message'),
        submitButton: document.getElementById('add-to-cart-button'),
        submitMessage: document.querySelector('.form-submit-message'),
        datePickerContainer: document.getElementById('date-picker-container'),
        dateTimeInput: document.getElementById('scheduled-datetime'),
        dateTimeISOInput: document.getElementById('scheduled-datetime-iso'),
        sendOptions: document.querySelectorAll('input[name="send-option"]')
    });
    
    // Utility functions
    const utils = {
        // Sanitize strings to prevent XSS
        sanitizeString(str) {
            if (!str) return '';
            const temp = document.createElement('div');
            temp.textContent = str;
            return temp.innerHTML;
        },
        
        // Debounce function to improve performance
        debounce(func, delay) {
            let timer;
            return function(...args) {
                clearTimeout(timer);
                timer = setTimeout(() => func.apply(this, args), delay);
            };
        },
        
        // Clear error messages
        clearErrorMessages(element) {
            const container = element.closest('.form-group');
            if (container) {
                const errorMessages = container.querySelectorAll('.error-message');
                errorMessages.forEach(el => el.remove());
            }
        },
        
        // Show validation errors
        showValidationError(input, message, skipGlobalMessage = false) {
            // Add error class to input
            input.classList.add('is-invalid');
            input.classList.remove('is-valid');
            
            // Clear existing error messages
            const container = input.closest('.form-group');
            if (container) {
                const errorMessages = container.querySelectorAll('.error-message');
                errorMessages.forEach(el => el.remove());
            } else {
                const errorMessages = input.parentNode.querySelectorAll('.error-message');
                errorMessages.forEach(el => el.remove());
            }
            
            // Create new error message element
            const errorElement = document.createElement('div');
            errorElement.classList.add('error-message');
            errorElement.textContent = message;
            
            // Insert after input or wrapper
            const insertAfter = input.closest('.amount-input-container') || input;
            insertAfter.parentNode.insertBefore(errorElement, insertAfter.nextSibling);
            
            // Show global message if needed
            if (!skipGlobalMessage && (message.includes('required') || message.includes('minimum') || message.includes('maximum'))) {
                utils.showMessage(message, 'error');
            }
            
            return false;
        },
        
        // Show global messages
        showMessage(message, type = 'success') {
            if (!elements.submitMessage) return;
            
            const colors = {
                success: '#23d160',
                error: '#ff3860',
                info: '#3273dc',
                warning: '#ffdd57'
            };
            
            elements.submitMessage.setAttribute('role', 'status');
            elements.submitMessage.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
            elements.submitMessage.innerHTML = `<span>${utils.sanitizeString(message)}</span>`;
            elements.submitMessage.classList.add('is-visible', `is-${type}`);
            elements.submitMessage.style.display = 'inline-block';
            elements.submitMessage.style.color = colors[type] || colors.info;
            
            if (elements.submitMessage._hideTimeout) {
                clearTimeout(elements.submitMessage._hideTimeout);
            }
            
            elements.submitMessage._hideTimeout = setTimeout(() => {
                elements.submitMessage.classList.remove('is-visible');
                elements.submitMessage.style.display = 'none';
                elements.submitMessage._hideTimeout = null;
            }, 5000);
        },
        
        // Generate CSRF token
        generateCSRFToken() {
            return Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
        },
        
        // Format date as YYYY-MM-DD
        formatDateForInput(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        
        // Format date as MM/DD/YYYY
        formatDateMMDDYYYY(date) {
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
        }
    };
    
    // Create global validation method for form submission
    window.showValidationError = utils.showValidationError;
    
    // Initialize components
    initCustomAmountHandler();
    initDatePicker();
    initSendOptions();
    initFormValidation();
    
    // Form submission is handled directly in the code below
    // No need to call a separate function
    
    /**
     * Global show validation error helper function
     * @param {HTMLElement} input - The input element with the error
     * @param {string} message - The error message
     * @param {boolean} skipGlobalMessage - If true, won't show in the global message area
     */
    function showValidationError(input, message, skipGlobalMessage = false) {
        // Add error class to input
        input.classList.add('is-invalid');
        
        // Clear any existing error messages for this input and its container
        const container = input.closest('.form-group');
        if (container) {
            const existingErrors = container.querySelectorAll('.error-message');
            existingErrors.forEach(el => el.remove());
        } else {
            const existingErrors = input.parentNode.querySelectorAll('.error-message');
            existingErrors.forEach(el => el.remove());
        }
        
        // Create new error message element
        const errorElement = document.createElement('div');
        errorElement.classList.add('error-message');
        errorElement.textContent = message;
        
        // Insert after input or after any wrapper containing the input
        const insertAfter = input.closest('.amount-input-container') || input;
        insertAfter.parentNode.insertBefore(errorElement, insertAfter.nextSibling);
        
        // Show error message with global function but only if not skipped
        // and if it's a major error type
        if (!skipGlobalMessage && (message.includes('required') || message.includes('minimum') || message.includes('maximum'))) {
            showMessage(message, 'error');
        }
        
        return false;
    }

    /**
     * Validate gift card amount with proper error feedback
     */
    function validateAmount(showError = true, formatOutput = false) {
        if (!elements.customAmountInput) return false;
        
        const input = elements.customAmountInput;
        const value = input.value.trim();
        const amount = parseFloat(value);
        const minAmount = CONSTANTS.MIN_AMOUNT;
        const maxAmount = CONSTANTS.MAX_AMOUNT;
        
        // Reset validation state
        input.classList.remove('is-valid', 'is-invalid');
        
        // Clear any existing error messages
        utils.clearErrorMessages(input);
        
        // Check if empty
        if (value === '') {
            if (showError) {
                utils.showValidationError(input, 'Please enter an amount', true);
            }
            return false;
        }
        
        // Check if valid number
        if (isNaN(amount)) {
            if (showError) {
                utils.showValidationError(input, 'Please enter a valid amount', true);
            }
            return false;
        }
        
        // Check minimum amount
        if (amount < minAmount) {
            if (showError) {
                utils.showValidationError(input, `Minimum amount is $${minAmount.toFixed(2)}`, true);
                if (formatOutput) {
                    input.value = minAmount.toFixed(2);
                } else {
                    input.value = String(minAmount);
                }
            }
            return false;
        }
        
        // Check maximum amount
        if (amount > maxAmount) {
            if (showError) {
                utils.showValidationError(input, `Maximum amount is $${maxAmount.toFixed(2)}`, true);
                if (formatOutput) {
                    input.value = maxAmount.toFixed(2);
                } else {
                    input.value = String(maxAmount);
                }
            }
            return false;
        }
        
        // Only format with decimal places if specified (typically on form submission)
        if (formatOutput) {
            input.value = amount.toFixed(2);
        }
        
        // Store sanitized value for later retrieval (always with decimal precision for backend)
        input.dataset.customAmount = amount.toFixed(2);
        input.classList.add('is-valid');
        
        return true;
    }
    
    /**
     * Initialize custom amount input with enhanced validation
     */
    function initCustomAmountHandler() {
        if (!elements.customAmountInput) return;
        
        // Update currency display when amount changes
        const updateCurrencyDisplay = () => {
            const amountContainer = elements.customAmountInput.closest('.amount-input-container');
            const currencySymbol = amountContainer?.querySelector('.currency-symbol');
            
            if (currencySymbol) {
                const hasValue = elements.customAmountInput.value.trim() !== '';
                currencySymbol.classList.toggle('has-value', hasValue);
            }
        };
        
        // Input handler with formatting
        elements.customAmountInput.addEventListener('input', function() {
            // Format the input value to ensure it's a valid number
            let inputValue = this.value;
            
            // Remove any non-numeric characters except decimal point
            inputValue = inputValue.replace(/[^0-9.]/g, '');
            
            // Clear any existing error messages when user starts typing
            utils.clearErrorMessages(this);
            
            // Ensure only one decimal point
            const decimalPoints = inputValue.match(/\./g);
            if (decimalPoints && decimalPoints.length > 1) {
                inputValue = inputValue.substring(0, inputValue.lastIndexOf('.'));
            }
            
            // Limit to two decimal places
            if (inputValue.includes('.')) {
                const parts = inputValue.split('.');
                if (parts[1] && parts[1].length > 2) {
                    inputValue = parts[0] + '.' + parts[1].substring(0, 2);
                }
            }
            
            // Update the input value
            this.value = inputValue;
            
            // Debounced validation during typing
            debounceValidate();
            
            // Update currency symbol display
            updateCurrencyDisplay();
        });
        
        // Create a safe wrapper for validation that only validates non-empty values
        const safeValidate = () => {
            const value = elements.customAmountInput.value.trim();
            // Only validate if there's content and it's not just a decimal point
            if (value !== '' && value !== '.') {
                validateAmount(false, false);
            }
        };
        
        // Create debounced validation function with delay to improve performance
        const debounceValidate = utils.debounce(safeValidate, CONSTANTS.DEBOUNCE_DELAY);
        
        // Validate amount on blur (focus lost)
        elements.customAmountInput.addEventListener('blur', function() {
            // Clear any existing error messages first
            utils.clearErrorMessages(this);
            
            // Only validate if there's an actual value entered
            const value = this.value.trim();
            if (value !== '') {
                validateAmount(true, false); // No need to format with .00 on blur
            }
            updateCurrencyDisplay();
        });
        
        // Add a focus event handler to clear errors when field gets focus
        elements.customAmountInput.addEventListener('focus', function() {
            // Remove error state and messages when field gets focus
            this.classList.remove('is-invalid');
            utils.clearErrorMessages(this);
        });
        
        // Initialize currency display
        updateCurrencyDisplay();
    }
    
    // Variant ID is now handled directly via the hidden input
    // which gets updated by the variant-radios component
    /**
     * Initialize date and time picker with enhanced security and validation
     */
    function initDatePicker() {
        if (!elements.dateTimeInput || !elements.dateTimeISOInput) return;
        
        // Get current date/time and add one day for default selection
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(12, 0, 0, 0); // Noon tomorrow
        
        // Configure date picker with secure options
        const flatpickrInstance = flatpickr(elements.dateTimeInput, {
            enableTime: true,          // Enable time selection
            dateFormat: "m/d/Y h:i K", // Format: mm/dd/yyyy hh:mm AM/PM
            minDate: new Date(),       // Prevent selecting dates in the past with exact current time
            defaultDate: tomorrow,     // Default to noon tomorrow
            defaultHour: 12,           // Default to noon
            minuteIncrement: 15,       // 15-minute intervals
            allowInput: true,          // Allow manual input
            time_24hr: false,          // Use 12-hour time with AM/PM
            disableMobile: false,      // Use native mobile pickers on mobile if available
            position: "auto",          // Position the calendar automatically
            locale: {
                firstDayOfWeek: 0       // Start week on Sunday
            },
            
            // When the date changes, update the ISO format input with validation
            onChange: function(selectedDates, dateStr, instance) {
                if (!selectedDates || selectedDates.length === 0) {
                    elements.dateTimeISOInput.value = '';
                    return;
                }
                
                const selectedDate = selectedDates[0];
                const now = new Date();
                
                // Ensure selected date is not in the past
                if (selectedDate < now) {
                    utils.showMessage('Please select a future date and time', 'error');
                    
                    // Set to next available time (rounded to 15 min)
                    const nextTime = new Date(now);
                    nextTime.setMinutes(Math.ceil(nextTime.getMinutes() / 15) * 15);
                    instance.setDate(nextTime);
                    return;
                }
                
                // Store ISO format in hidden input for form submission
                elements.dateTimeISOInput.value = selectedDate.toISOString();
                
                // Add validation class
                elements.dateTimeInput.classList.add('is-valid');
                elements.dateTimeInput.classList.remove('is-invalid');
            }
        });
        
        // Handle calendar icon click - improved accessibility
        const calendarIcon = document.querySelector('.calendar-icon');
        if (calendarIcon) {
            // Add ARIA attributes for accessibility
            calendarIcon.setAttribute('role', 'button');
            calendarIcon.setAttribute('aria-label', 'Open calendar');
            calendarIcon.setAttribute('tabindex', '0');
            
            // Handle click and keyboard events
            const openCalendar = function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                // Open the Flatpickr calendar if it exists
                if (flatpickrInstance) {
                    flatpickrInstance.open();
                    // Focus on the calendar for keyboard navigation
                    setTimeout(() => {
                        const calendar = document.querySelector('.flatpickr-calendar');
                        if (calendar) calendar.focus();
                    }, 100);
                }
            };
            
            // Add click handler
            calendarIcon.addEventListener('click', openCalendar);
            
            // Add keyboard handler for Enter key
            calendarIcon.addEventListener('keydown', function(e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    openCalendar(e);
                }
            });
        }
        
        // Validate manually entered date/time with enhanced error handling
        elements.dateTimeInput.addEventListener('blur', function() {
            if (this.value && !flatpickrInstance.selectedDates.length) {
                try {
                    // Parse the user input date
                    const userDate = new Date(this.value);
                    const now = new Date();
                    
                    if (!isNaN(userDate.getTime())) {
                        // Check if date is in the past
                        if (userDate < now) {
                            showValidationError(this, 'Please select a future date and time');
                            this.value = '';
                            elements.dateTimeISOInput.value = '';
                        } else {
                            // Valid future date, set it in Flatpickr
                            flatpickrInstance.setDate(userDate);
                        }
                    } else {
                        // Invalid date format
                        showValidationError(this, 'Please enter a valid date and time');
                        this.value = '';
                        elements.dateTimeISOInput.value = '';
                    }
                } catch (e) {
                    showValidationError(this, 'Please enter a valid date and time');
                    this.value = '';
                    elements.dateTimeISOInput.value = '';
                }
            }
        });
        
        // Utility function to show validation error
        function showValidationError(input, message) {
            input.classList.add('is-invalid');
            input.classList.remove('is-valid');
            showMessage(message, 'error');
        }
        
        return flatpickrInstance;
    }
    
    // Format date as YYYY-MM-DD for standard format
    function formatDateForInput(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }
    
    // Format date as MM/DD/YYYY for display
    function formatDateMMDDYYYY(date) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }
    
    /**
     * Initialize send options (now/later) radio buttons
     */
    function initSendOptions() {
        if (!elements.sendOptions || !elements.datePickerContainer) return;
        
        // Show/hide date picker based on radio selection with animation
        elements.sendOptions.forEach(option => {
            option.addEventListener('change', function() {
                const isLater = this.value === 'later';
                
                // Animation for better UX
                if (isLater) {
                    // Show with animation
                    elements.datePickerContainer.style.opacity = '0';
                    elements.datePickerContainer.style.display = 'block';
                    setTimeout(() => {
                        elements.datePickerContainer.style.opacity = '1';
                    }, 50);
                    
                    // Set focus on the date input for better accessibility
                    setTimeout(() => {
                        if (elements.dateTimeInput) elements.dateTimeInput.focus();
                    }, 300);
                } else {
                    // Hide with animation
                    elements.datePickerContainer.style.opacity = '0';
                    setTimeout(() => {
                        elements.datePickerContainer.style.display = 'none';
                    }, 300);
                    
                    // Clear the date-time fields
                    if (elements.dateTimeInput) elements.dateTimeInput.value = '';
                    if (elements.dateTimeISOInput) elements.dateTimeISOInput.value = '';
                    
                    // Remove validation classes
                    if (elements.dateTimeInput) {
                        elements.dateTimeInput.classList.remove('is-valid', 'is-invalid');
                    }
                }
            });
        });
        
        // Set initial visibility based on selected option
        const isLaterSelected = document.querySelector('input[name="send-option"][value="later"]')?.checked || false;
        elements.datePickerContainer.style.display = isLaterSelected ? 'block' : 'none';
        elements.datePickerContainer.style.opacity = isLaterSelected ? '1' : '0';
        
        // Ensure transitions work smoothly
        elements.datePickerContainer.style.transition = 'opacity 0.3s ease';
    }
    
    /**
     * Initialize comprehensive form validation
     */
    function initFormValidation() {
        if (!elements.form) return;
        
        // Enhanced email validation function with comprehensive checks
        const validateEmail = (email) => {
            const emailStr = String(email).trim().toLowerCase();
            
            // Basic format check
            if (!CONSTANTS.EMAIL_REGEX.test(emailStr)) {
                return { valid: false, message: 'Please enter a valid email address' };
            }
            
            // Split email into parts
            const emailParts = emailStr.split('@');
            if (emailParts.length !== 2) {
                return { valid: false, message: 'Email must contain exactly one @ symbol' };
            }
            
            const [localPart, domainPart] = emailParts;
            
            // Check local part (before @)
            if (localPart.length === 0) {
                return { valid: false, message: 'Email address is incomplete' };
            }
            
            // Check if local part starts or ends with a dot
            if (localPart.startsWith('.') || localPart.endsWith('.')) {
                return { valid: false, message: 'Email address cannot start or end with a dot before @' };
            }
            
            // Check for consecutive dots
            if (localPart.includes('..')) {
                return { valid: false, message: 'Email address cannot contain consecutive dots' };
            }
            
            // Check domain part (after @)
            if (domainPart.length === 0) {
                return { valid: false, message: 'Email domain is missing' };
            }
            
            // Check if domain has at least one dot (TLD requirement)
            if (!domainPart.includes('.')) {
                // Check if this matches a common provider without TLD
                const suggestion = CONSTANTS.COMMON_EMAIL_PROVIDERS.find(provider => 
                    provider.common.includes(domainPart)
                );
                
                if (suggestion) {
                    return { 
                        valid: false, 
                        message: `Did you mean ${suggestion.correct}? Email domain appears incomplete.`
                    };
                }
                
                return { valid: false, message: 'Email domain must include a valid extension (e.g., .com, .net, .org)' };
            }
            
            // Extract TLD (everything after the last dot)
            const domainSegments = domainPart.split('.');
            const tld = domainSegments[domainSegments.length - 1];
            
            // Check if TLD is valid (at least 2 characters)
            if (tld.length < 2) {
                return { valid: false, message: 'Email domain extension is too short' };
            }
            
            // Check if TLD contains only letters
            if (!/^[a-z]+$/.test(tld)) {
                return { valid: false, message: 'Email domain extension must contain only letters' };
            }
            
            // Optional: Check for common typos in email providers
            const providerSuggestion = CONSTANTS.COMMON_EMAIL_PROVIDERS.find(provider => 
                provider.common.includes(domainPart)
            );
            
            if (providerSuggestion) {
                return { 
                    valid: false, 
                    message: `Did you mean ${providerSuggestion.correct}? Please check the email domain.`
                };
            }
            
            // Additional check: domain part should not start or end with a dot or hyphen
            if (domainPart.startsWith('.') || domainPart.endsWith('.') || 
                domainPart.startsWith('-') || domainPart.endsWith('-')) {
                return { valid: false, message: 'Email domain format is invalid' };
            }
            
            // Check that domain segments (between dots) are not empty
            if (domainSegments.some(segment => segment.length === 0)) {
                return { valid: false, message: 'Email domain contains empty segments' };
            }
            
            return { valid: true, message: '' };
        };
        
        // Update the global validation function reference to use our utils object
        window.showValidationError = utils.showValidationError;
        
        // Validate a required field
        const validateRequired = (input, fieldName) => {
            // Clear any existing error messages
            utils.clearErrorMessages(input);
            
            if (!input.value.trim()) {
                return utils.showValidationError(input, `${fieldName} is required`);
            }
            input.classList.remove('is-invalid');
            input.classList.add('is-valid');
            return true;
        };
        
        // Validate email format
        const validateEmailField = (input, fieldName) => {
            if (!validateRequired(input, fieldName)) return false;
            
            const emailValidation = validateEmail(input.value.trim());
            if (!emailValidation.valid) {
                return utils.showValidationError(input, emailValidation.message);
            }
            
            // If this is the recipient email confirmation, check if it matches
            if (input.id === 'recipient-email-confirm' && elements.recipientEmail) {
                return validateEmailsMatch();
            }
            
            // If this is the main recipient email, validate the confirmation if it has content
            if (input.id === 'recipient-email' && 
                elements.recipientEmailConfirm && 
                elements.recipientEmailConfirm.value.trim()) {
                validateEmailsMatch();
            }
            
            input.classList.remove('is-invalid');
            input.classList.add('is-valid');
            return true;
        };
        
        // Validate that recipient emails match
        const validateEmailsMatch = () => {
            // Skip validation if email fields don't exist
            if (!elements.recipientEmail || !elements.recipientEmailConfirm) {
                return true;
            }
            
            const email1 = elements.recipientEmail.value.trim().toLowerCase();
            const email2 = elements.recipientEmailConfirm.value.trim().toLowerCase();
            
            // Skip validation if either field is empty
            if (!email1 || !email2) {
                if (elements.emailMatchIndicator) {
                    elements.emailMatchIndicator.textContent = '';
                }
                return true;
            }
            
            if (email1 === email2) {
                // Emails match - clear any error messages
                elements.recipientEmailConfirm.classList.remove('is-invalid');
                elements.recipientEmailConfirm.classList.add('is-valid');
                
                // Update match indicator if it exists (product page)
                if (elements.emailMatchIndicator) {
                    elements.emailMatchIndicator.innerHTML = '<span>✓</span><span>Emails match</span>';
                    elements.emailMatchIndicator.classList.add('match-success');
                    elements.emailMatchIndicator.classList.remove('match-error');
                }
                
                // Remove any error message elements if they exist
                const errorMessage = elements.recipientEmailConfirm.nextElementSibling;
                if (errorMessage && errorMessage.classList.contains('error-message')) {
                    errorMessage.remove();
                }
                
                return true;
            } else {
                // Emails don't match
                utils.showValidationError(elements.recipientEmailConfirm, 'Emails do not match');
                
                // Update match indicator if it exists (product page)
                if (elements.emailMatchIndicator) {
                    elements.emailMatchIndicator.innerHTML = '<span>✗</span><span>Emails do not match</span>';
                    elements.emailMatchIndicator.classList.add('match-error');
                    elements.emailMatchIndicator.classList.remove('match-success');
                }
                
                return false;
            }
        };
        
        // Add blur event validation for all required fields
        if (elements.senderName) {
            elements.senderName.addEventListener('blur', function() {
                validateRequired(this, 'Your name');
            });
            
            // Add input event to clear error when user types
            elements.senderName.addEventListener('input', function() {
                if (this.value.trim()) {
                    this.classList.remove('is-invalid');
                    this.classList.add('is-valid');
                    const errorElements = this.parentNode.querySelectorAll('.error-message');
                    errorElements.forEach(el => el.remove());
                }
            });
        }
        
        if (elements.senderEmail) {
            elements.senderEmail.addEventListener('blur', function() {
                validateEmailField(this, 'Your email');
            });
        }
        
        if (elements.recipientName) {
            elements.recipientName.addEventListener('blur', function() {
                validateRequired(this, "Recipient's name");
            });
        }
        
        if (elements.recipientEmail) {
            elements.recipientEmail.addEventListener('blur', function() {
                validateEmailField(this, "Recipient's email");
            });
            
            // Add input event for real-time validation when typing
            elements.recipientEmail.addEventListener('input', function() {
                // Only validate match if confirmation has a value
                if (elements.recipientEmailConfirm && elements.recipientEmailConfirm.value.trim()) {
                    validateEmailsMatch();
                }
            });
        }
        
        // Add validation for email confirmation field
        if (elements.recipientEmailConfirm) {
            elements.recipientEmailConfirm.addEventListener('blur', function() {
                validateEmailField(this, "Confirm recipient's email");
            });
            
            // Add input event for real-time validation when typing
            elements.recipientEmailConfirm.addEventListener('input', function() {
                // Only validate if both fields have values
                if (elements.recipientEmail && elements.recipientEmail.value.trim()) {
                    validateEmailsMatch();
                }
            });
        }
        
        // Validate the message field (optional but with max length)
        if (elements.message) {
            elements.message.addEventListener('input', function() {
                const maxLength = 500;
                if (this.value.length > maxLength) {
                    this.value = this.value.substring(0, maxLength);
                    showValidationError(this, `Message cannot exceed ${maxLength} characters`);
                } else {
                    this.classList.remove('is-invalid');
                }
            });
        }
        
        // Create validateGiftCardForm function and expose it globally
        const validateGiftCardForm = function() {
            let isValid = true;
            
            // Validate amount
            if (elements.customAmountInput) {
                // Use the validateAmount function with formatOutput=true for submission
                const isAmountValid = validateAmount(true, true);
                isValid = isAmountValid && isValid;
            }
            
            // Validate required fields
            if (elements.senderName) {
                isValid = validateRequired(elements.senderName, 'Your name') && isValid;
            }
            
            if (elements.senderEmail) {
                isValid = validateEmailField(elements.senderEmail, 'Your email') && isValid;
            }
            
            if (elements.recipientName) {
                isValid = validateRequired(elements.recipientName, "Recipient's name") && isValid;
            }
            
            if (elements.recipientEmail) {
                isValid = validateEmailField(elements.recipientEmail, "Recipient's email") && isValid;
            }
            
            // Check that confirmation email is valid and matches
            if (elements.recipientEmailConfirm) {
                const confirmValid = validateEmailField(elements.recipientEmailConfirm, "Confirm recipient's email");
                isValid = confirmValid && isValid;
                
                // Explicitly check match if both fields have values
                if (confirmValid && 
                    elements.recipientEmail && 
                    elements.recipientEmail.value.trim() && 
                    elements.recipientEmailConfirm.value.trim()) {
                    
                    const emailsMatch = validateEmailsMatch();
                    isValid = emailsMatch && isValid;
                }
            }
            
            // Check if send later is selected and validate date
            const sendLater = document.querySelector('input[name="send-option"][value="later"]')?.checked || false;
            if (sendLater && elements.dateTimeInput) {
                if (!elements.dateTimeInput.value) {
                    utils.showValidationError(elements.dateTimeInput, 'Please select a date and time');
                    isValid = false;
                }
            }
            
            return isValid;
        };
        
        // Expose validation function globally
        window.validateGiftCardForm = validateGiftCardForm;
    }
    
    /**
     * Handle form submission with comprehensive validation and security
     */
    if (elements.form && elements.submitButton) {
        // Generate a unique token for CSRF protection
        const generateCSRFToken = () => {
            return Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
                .map(b => b.toString(16).padStart(2, '0'))
                .join('');
        };
        
        // Add CSRF token to the form
        const csrfToken = generateCSRFToken();
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'csrf_token';
        csrfInput.value = csrfToken;
        elements.form.appendChild(csrfInput);
        
        // Store CSRF token in sessionStorage
        try {
            sessionStorage.setItem('giftcard_csrf_token', csrfToken);
        } catch (e) {
            console.warn('Unable to store CSRF token in sessionStorage');
        }
        
        // Form submission handler with enhanced security and error handling
        elements.form.addEventListener('submit', function(event) {
            // Prevent default form submission
            event.preventDefault();
            
            // Check if rate limited
            const now = Date.now();
            if (now - CONSTANTS.RATE_LIMIT.lastSubmit < CONSTANTS.RATE_LIMIT.timeWindow) {
                // Within time window, increment count
                CONSTANTS.RATE_LIMIT.count++;
            } else {
                // Outside time window, reset count
                CONSTANTS.RATE_LIMIT.count = 1;
                CONSTANTS.RATE_LIMIT.lastSubmit = now;
            }
            
            // Check if we've exceeded rate limit
            if (CONSTANTS.RATE_LIMIT.count > CONSTANTS.RATE_LIMIT.maxSubmits) {
                utils.showMessage('Too many submissions. Please try again later.', 'error');
                return;
            }
            
            // Verify CSRF token
            const formToken = csrfInput.value;
            const storedToken = sessionStorage.getItem('giftcard_csrf_token');
            if (!formToken || formToken !== storedToken) {
                console.error('CSRF token validation failed');
                utils.showMessage('Security validation failed. Please refresh the page.', 'error');
                return;
            }
            
            // Comprehensive validation
            if (!window.validateGiftCardForm()) {
                return; // Validation function will show appropriate error messages
            }
            
            // Format the amount field with decimal places for submission
            if (elements.customAmountInput && elements.customAmountInput.value.trim()) {
                // Format the input with decimal places for submission
                const amount = parseFloat(elements.customAmountInput.value.trim());
                if (!isNaN(amount)) {
                    elements.customAmountInput.value = amount.toFixed(2);
                }
            }
            
            // Get field values with sanitization
            const fieldValues = {
                senderName: utils.sanitizeString(elements.senderName?.value || ''),
                senderEmail: utils.sanitizeString(elements.senderEmail?.value || '').toLowerCase(),
                recipientName: utils.sanitizeString(elements.recipientName?.value || ''),
                recipientEmail: utils.sanitizeString(elements.recipientEmail?.value || '').toLowerCase(),
                message: utils.sanitizeString(elements.message?.value || ''),
                variantId: elements.variantInput?.value,
                customAmount: elements.customAmountInput ? parseFloat(elements.customAmountInput.value) : 0,
                sendLater: document.querySelector('input[name="send-option"][value="later"]')?.checked || false,
                scheduledDateTime: elements.dateTimeInput?.value || '',
                scheduledDateTimeISO: elements.dateTimeISOInput?.value || ''
            };
            
            // Final validation checks
            if (!fieldValues.variantId) {
                utils.showMessage('Unable to process gift card. Please refresh and try again.', 'error');
                return;
            }
            
            // Ensure the amount is valid
            if (isNaN(fieldValues.customAmount)) {
                utils.showMessage('Please enter a valid amount', 'error');
                return;
            }
            
            // Check minimum amount with more descriptive message
            if (fieldValues.customAmount < CONSTANTS.MIN_AMOUNT) {
                // Set the input to the minimum value for better UX
                if (elements.customAmountInput) {
                    elements.customAmountInput.value = CONSTANTS.MIN_AMOUNT.toFixed(2);
                }
                utils.showMessage(`Amount must be at least $${CONSTANTS.MIN_AMOUNT.toFixed(2)}`, 'error');
                return;
            }
            
            if (fieldValues.sendLater && !fieldValues.scheduledDateTime) {
                utils.showMessage('Please select a date and time for scheduled delivery', 'error');
                return;
            }
            
            // Show loading state
            elements.submitButton.disabled = true;
            elements.submitButton.classList.add('is-loading');
            elements.submitButton.innerHTML = '<span class="loading-spinner"></span> Adding...';
            
            // Prepare properties object with sanitized values
            const properties = {
                'Sender Name': fieldValues.senderName,
                'Sender Email': fieldValues.senderEmail,
                'Recipient Name': fieldValues.recipientName,
                'Recipient Email': fieldValues.recipientEmail,
                '_custom_price': fieldValues.customAmount.toFixed(2) // Special property for Shopify Functions
            };
            
            // Add optional properties
            if (fieldValues.message) properties['Message'] = fieldValues.message;
            if (fieldValues.sendLater) {
                properties['Send Later'] = 'Yes';
                properties['Scheduled Time'] = fieldValues.scheduledDateTime;
                if (fieldValues.scheduledDateTimeISO) {
                    properties['Scheduled Time ISO'] = fieldValues.scheduledDateTimeISO;
                }
            } else {
                properties['Send Later'] = 'No';
            }
            
            // Add to cart with enhanced error handling
            addToCart(fieldValues.variantId, 1, properties);
            
            // Generate new CSRF token for next submission
            const newToken = generateCSRFToken();
            csrfInput.value = newToken;
            try {
                sessionStorage.setItem('giftcard_csrf_token', newToken);
            } catch (e) {
                console.warn('Unable to update CSRF token');
            }
        });
    }
    
    /**
     * Add to cart with enhanced security and error handling
     */
    function addToCart(variantId, quantity, properties) {
        // Reset button state function to avoid code duplication
        const resetButton = (success) => {
            if (!elements.submitButton) return;
            
            elements.submitButton.disabled = false;
            elements.submitButton.classList.remove('is-loading');
            elements.submitButton.textContent = 'ADD TO CART';
            
            if (success) {
                // Show success message with auto-clear
                utils.showMessage('Gift card added to cart!', 'success');
                
                // Update cart with animation
                const updatePromise = refreshCart();
                
                // After cart update, open drawer with proper timing
                updatePromise
                    .then(() => {
                        // Add a small delay to ensure DOM updates are complete
                        setTimeout(() => openCartDrawer(), 150);
                    })
                    .catch(() => {
                        // Still try to open drawer even if refresh fails
                        setTimeout(() => openCartDrawer(), 150);
                    });
            } else {
                utils.showMessage('Error adding to cart. Please try again.', 'error');
            }
        };
        
        // Security checks
        if (!variantId || !quantity) {
            resetButton(false);
            return;
        }
        
        // Add item to cart with security headers and timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                id: variantId,
                quantity: quantity,
                properties: properties
            }),
            signal: controller.signal
        })
        .then(response => {
            clearTimeout(timeoutId);
            if (!response.ok) {
                return response.json().then(json => {
                    throw new Error(json.description || `Error ${response.status}`);
                });
            }
            return response.json();
        })
        .then(data => {
            if (data.message === 'Bad Request') {
                throw new Error('Unable to add to cart. Please check your inputs.');
            }
            resetButton(true);
        })
        .catch(error => {
            clearTimeout(timeoutId);
            console.error('Add to cart error:', error);
            utils.showMessage(error.message || 'Error adding to cart. Please try again.', 'error');
            resetButton(false);
        });
    }
    
    /**
     * Refresh cart data with optimized performance
     * @returns {Promise} Promise that resolves when cart is refreshed
     */
    function refreshCart() {
        return new Promise((resolve, reject) => {
            // Use debouncing to prevent multiple refreshes
            if (window._refreshCartPromise) {
                return window._refreshCartPromise;
            }
            
            // Create a promise that we'll save for debouncing
            const promise = fetch('/cart.js', {
                headers: { 'Accept': 'application/json' }
            })
            .then(response => response.json())
            .then(cartData => {
                // Update cart count bubbles first
                const cartCountElements = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
                cartCountElements.forEach(el => {
                    if (el) el.textContent = cartData.item_count;
                });
                
                // Get the sections to render using theme's system
                const sectionsToRender = [
                    {
                        id: 'mini-cart',
                        section: 'mini-cart',
                        selector: '.shopify-section'
                    },
                    {
                        id: 'cart-icon-bubble', 
                        section: 'cart-icon-bubble',
                        selector: '.shopify-section'
                    },
                    {
                        id: 'mobile-cart-icon-bubble',
                        section: 'mobile-cart-icon-bubble', 
                        selector: '.shopify-section'
                    }
                ];
                
                // Get all section IDs to fetch
                const sections = sectionsToRender.map(section => section.section);
                const sectionsUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
                
                // Fetch and update all sections
                return fetch(sectionsUrl)
                    .then(response => response.json())
                    .then(sectionsData => {
                        // Update each section using theme's method
                        sectionsToRender.forEach(section => {
                            const sectionElement = document.getElementById(section.id);
                            const sectionHtml = sectionsData[section.section];
                            
                            if (sectionElement && sectionHtml) {
                                // Use the same method as the theme's cart system
                                const tempDiv = document.createElement('div');
                                tempDiv.innerHTML = sectionHtml;
                                const sectionContent = tempDiv.querySelector(section.selector);
                                
                                if (sectionContent) {
                                    sectionElement.innerHTML = sectionContent.innerHTML;
                                }
                            }
                        });
                        
                        // Dispatch cart update event for other components
                        document.dispatchEvent(new CustomEvent('cart:updated', { 
                            detail: { cartData } 
                        }));
                        
                        return cartData;
                    });
            })
            .finally(() => {
                // Clear the saved promise after a short delay
                setTimeout(() => {
                    window._refreshCartPromise = null;
                }, 300);
            });
            
            // Save the promise for debouncing
            window._refreshCartPromise = promise;
            
            // Return the promise to the caller
            promise.then(resolve).catch(reject);
        });
    }
    
    /**
     * Open cart drawer with improved UX and browser compatibility
     */
    function openCartDrawer() {
        // Try multiple selectors to find the cart toggle button
        const cartToggleSelectors = [
            '#cart-icon-bubble',
            '[data-action="open-drawer"][data-drawer-id="cart"]',
            '.js-drawer-open-cart', 
            '.cart-link',
            '.cart-drawer-trigger',
            '.header__icon--cart'
        ];
        
        // Find the first matching cart toggle
        let cartToggle = null;
        for (const selector of cartToggleSelectors) {
            cartToggle = document.querySelector(selector);
            if (cartToggle) break;
        }
        
        // Try to open the cart drawer in different ways
        if (cartToggle) {
            // Try click event first
            cartToggle.click();
        } else {
            // Try theme-specific methods
            const miniCart = document.querySelector('mini-cart');
            if (miniCart && typeof miniCart.open === 'function') {
                miniCart.open();
            } else if (window.theme?.cart?.openDrawer instanceof Function) {
                window.theme.cart.openDrawer();
            } else {
                // Try to find and open cart drawer directly
                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer) {
                    const detailsElement = cartDrawer.querySelector('details');
                    if (detailsElement && !detailsElement.hasAttribute('open')) {
                        detailsElement.setAttribute('open', true);
                    }
                }
                
                // Fallback: dispatch custom events that themes might listen for
                document.dispatchEvent(new CustomEvent('cart:open'));
                document.dispatchEvent(new CustomEvent('cartdrawer:open'));
            }
        }
    }
    

});
